import {isPlatformBrowser} from '@angular/common';
import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID, ViewEncapsulation} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Cart} from '@spartacus/cart/base/root';
import {PaymentDetails, RoutingService, User, UserIdService} from '@spartacus/core';
import {BehaviorSubject, combineLatest, Observable, of, Subscription, throwError} from 'rxjs';
import {catchError, filter, first, map, skip, switchMap, take} from 'rxjs/operators';
import {ConfigService, DcpApiService, FeatureTogglesService} from '@dcp/configurator';
import {CheckoutEntryValidationService} from '@app/checkout/services/checkout-entry-validation.service';
import {RefreshCartService} from '@app/checkout/services/refresh-cart.service';
import {UploadAttachmentsService} from '@app/digital-native-checkout/tax-information/services/upload-attachments.service';
import {AddressIdentity} from '@app/model/dcp-address-identity';
import {TaxIdValidationPayload} from '@app/model/dcp-config-service/tax-config';
import {AddressUsage, CheckoutReviewOrderComponentTexts} from '@app/model/order/dcp-order-entry.model';
import {PaymentTypeCode} from '@app/model/order/dcp-order.model';
import {TemplateView} from '@app/model/template-view.model';
import {DcpCcPspService} from '@app/payment/services/dcp-cc-psp.service';
import {DcpCheckoutPaymentService} from '@app/payment/services/dcp-checkout-payment.service';
import {B2bUnitService} from '@app/services/b2b-unit.service';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {CustomScrollingService} from '@app/services/custom-scrolling.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {CheckoutPaymentFacade} from '@spartacus/checkout/base/root';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {CartDecoratorErrorHandlingService} from '@app/checkout/services/cart-decorator-error-handling.service';

@UntilDestroy()
@Component({
  selector: 'dcp-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SummaryComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() selectedView: TemplateView;
  @Input() componentTexts: CheckoutReviewOrderComponentTexts;
  @Input() entries: OrderEntry[];
  @Input() taxValidationSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  @Output() b2bUnitUpdateCallFailed: EventEmitter<boolean> = new EventEmitter<boolean>();
  currentPaymentProvider: string;
  view = TemplateView;
  isLoadingSubscription: Subscription = Subscription.EMPTY;
  cartIsLoading: boolean = false;
  taxExemptionCountryIsoCodes: string[] = this.configService.getStringArray('checkout.taxExemptionCountriesIsoCodes', []);
  userFromTaxExemptionCountry: boolean = this.taxExemptionCountryIsoCodes?.includes(this.countrySelectorService.getCurrentCountry().isocode);
  taxIdValidationPayload: TaxIdValidationPayload;
  taxExemptionCheckboxStatus: boolean;
  hasTaxDocument: boolean;
  isBrowser: boolean = isPlatformBrowser(this.platformId);
  isValidAndHasTaxDocumentForm: boolean = false;
  hasOptionAndFromTaxCountry: boolean = false;
  activeCart: Cart;
  selectedPaymentType: PaymentTypeCode;
  showSpinner: boolean = false;
  isNoCouponPromoError: boolean = false;
  isCouponSectionVisible: boolean;
  private currentUserUid: string;
  private isAddNewCardComponentClosed: boolean;

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    public checkoutEntryValidationService: CheckoutEntryValidationService,
    public uploadAttachmentsService: UploadAttachmentsService,
    public configService: ConfigService,
    public countrySelectorService: CountrySelectorService,
    public changeDetectorRef: ChangeDetectorRef,
    public dcpCcPspService: DcpCcPspService,
    public cartDecoratorErrorHandlingService: CartDecoratorErrorHandlingService,
    protected activeCartService: DcpActiveCartService,
    protected routingService: RoutingService,
    private scrollService: CustomScrollingService,
    private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
    private b2bUnitService: B2bUnitService,
    private refreshCartService: RefreshCartService,
    private userIdService: UserIdService,
    private dcpConfigService: DcpConfigService,
    private dcpApiService: DcpApiService,
    private checkoutPaymentFacade: CheckoutPaymentFacade,
    private featureToggleService: FeatureTogglesService
  ) {
  }

  ngOnInit() {
    this.subscribeToCurrentPaymentProvider();
    this.subscribeToLoadingState();
    this.subscribeToTaxValidation();
    this.resetInvalidCouponError();
    this.uploadAttachmentsService.getTaxExemptionCheckboxStatusSubject()
      .pipe(untilDestroyed(this)).subscribe(res => this.taxExemptionCheckboxStatus = res);
    this.uploadAttachmentsService.hasTaxAttachments()
      .pipe(untilDestroyed(this)).subscribe(res => this.hasTaxDocument = res);
    this.dcpCcPspService.isAddNewCreditCardComponentClosed().pipe(untilDestroyed(this)).subscribe(isClosed => {
      this.isAddNewCardComponentClosed = isClosed;
    });
    this.activeCartService.getActiveCart()
      .pipe(untilDestroyed(this)).subscribe((cart: Cart) => {
      this.activeCart = cart;
      this.checkCouponSectionIsVisible();
    });
    this.userIdService.getUserId()
      .pipe(untilDestroyed(this)).subscribe((uid: string) => this.currentUserUid = uid);
    this.dcpCheckoutPaymentService.getCurrentPaymentType().pipe(untilDestroyed(this)).subscribe((typeCode) => {
      this.selectedPaymentType = typeCode;
    });
  }

  ngOnDestroy() {
    this.isLoadingSubscription.unsubscribe();
  }

  /**
   * disables checkout and place order button as long as the cart is reloading.
   */
  subscribeToLoadingState() {
    if (this.isLoadingSubscription.closed) {
      this.isLoadingSubscription = this.activeCartService.getLoading().pipe(untilDestroyed(this)).subscribe(isLoading => {
        this.cartIsLoading = isLoading;
      });
    }
  }

  /**
   * updates tax validation status object
   */
  subscribeToTaxValidation() {
    this.taxValidationSubject.pipe(untilDestroyed(this)).subscribe(taxValidationResult => {
      if (taxValidationResult) {
        this.showSpinner = taxValidationResult.showSpinner || taxValidationResult.isValidationStarted;
        this.changeDetectorRef.detectChanges();
        if (taxValidationResult.taxIdValidationPayload) {
          this.taxIdValidationPayload = taxValidationResult.taxIdValidationPayload;
        }
      }
    });
  }

  /**
   *  Method checks attachments
   *  Returns true if has valid tax document
   *  or if tax exemption feature is disabled or user is not from tax exemption country
   *  or if taxExemptionCheckboxStatus is false
   */
  isDocumentValidForCheckout(): boolean {
    this.isValidAndHasTaxDocumentForm = this.uploadAttachmentsService.Attachments?.valid && this.uploadAttachmentsService.Attachments.length > 0;
    this.hasOptionAndFromTaxCountry = this.userFromTaxExemptionCountry;
    return this.hasTaxDocument && this.isValidAndHasTaxDocumentForm || !this.hasOptionAndFromTaxCountry || !this.taxExemptionCheckboxStatus;
  }

  isReadyForCheckout(): boolean {
    return this.paymentTypeIsCc() ? this.isAddNewCardComponentClosed && this.isDocumentValidForCheckout() : this.isDocumentValidForCheckout();
  }

  paymentTypeIsCc(): boolean {
    return this.selectedPaymentType === PaymentTypeCode.CARD;
  }

  /**
   * Checks when 'continue to checkout' button clicked when add new card component is opened.
   * if yes, sets error message flag to 'true', so all the consumers will show error messages
   * e.g. dcp-payment-method-credit-card
   */
  changeCCStatuses() {
    if (this.isBrowser && !this.isAddNewCardComponentClosed) {
      this.dcpCcPspService.setErrorMessageStatus(true);
      this.scrollService.scrollToElement(document.querySelector('.paymentFormEntry'));
    }
  }

  changeAttachmentsStatuses() {
    if (this.taxExemptionCheckboxStatus && !this.hasTaxDocument && !this.isValidAndHasTaxDocumentForm) {
      this.uploadAttachmentsService.showWarningMessageSubject.next(true);
      this.uploadAttachmentsService.hasTaxAttachmentsSubject.next(false);
      if (this.isBrowser) {
        this.scrollService.scrollToElement(document.querySelector('.ds-upload'));
      }
    }
  }

  storeCreditCardPaymentDetails(): void {
    this.dcpCheckoutPaymentService.getCurrentPaymentDetails()
      .pipe(take(1), filter(currentPaymentDetails => !!currentPaymentDetails), untilDestroyed(this))
      .subscribe((currentPaymentDetails: PaymentDetails) => {
        this.checkoutPaymentFacade.setPaymentDetails(currentPaymentDetails);
      });
  }

  /**
   * Submitting current page, and making tax id validation call <b>only if</b>
   * it was not yet done by blur event of tax id field.
   * Tax validation payload can only be present if tax id validation is required
   */
  checkoutReviewOrder(): void {
    this.showSpinner = true;
    this.changeDetectorRef.detectChanges();
    if (this.taxIdValidationPayload) {
      this.taxValidationSubject.next({
        isSummaryValidationPerformed: true,
        showSpinner: this.showSpinner
      });
      this.validateTaxIds();
    } else {
      this.processSubmitCheckout();
    }
  }

  /**
   * Subscribe to tax id validation (executed only if tax id should be validated)
   */
  validateTaxIds(): void {
    this.dcpConfigService.validateTaxIds(this.taxIdValidationPayload).pipe(untilDestroyed(this)).subscribe(
      () => {
        this.taxValidationSubject.next({
          isValidationError: false,
          successCall: true,
          showSpinner: true
        });
        this.processSubmitCheckout();
      },
      error => {
        this.taxValidationSubject.next({
          isValidationError: true,
          errorObject: error
        });
        this.handleNotReadyCheckout();
        if (this.isBrowser) {
          this.scrollService.scrollToElement(document.querySelector('.ds-invalid'));
        }
      }
    );
  }

  processSubmitCheckout(): void {
    this.getCurrentUser().pipe(untilDestroyed(this)).subscribe((user: User) => {
      if (this.isReadyForCheckout()) {
        if (this.paymentTypeIsCc()) {
          this.storeCreditCardPaymentDetails();
        }
        this.updateB2bUnit().pipe(take(1), untilDestroyed(this)).subscribe(() => {
          this.uploadAttachmentsService.continueToCheckoutSubject.next({
            status: true,
            userId: user?.uid,
            customerId: user?.customerId
          });
          // if the country does not require tax validation (hence the taxPayload is undefined),
          // just proceed to the next page
          if (!this.taxIdValidationPayload) {
            this.proceedToCheckoutReviewPage();
          }
          //if request to save taxData on the b2b customer is completed move forward
          this.uploadAttachmentsService.getB2bCustomerUpdateCallCompletedSubject()
            .pipe(untilDestroyed(this)).subscribe(b2bCustomerCallCompleted => {
            if (b2bCustomerCallCompleted) {
              this.proceedToCheckoutReviewPage();
            }
          });
        });
      } else {
        this.handleNotReadyCheckout();
      }
    });
  }

  proceedToCheckoutReviewPage() {
    this.dcpCcPspService.setErrorMessageStatus(false);
    this.routingService.go({cxRoute: 'checkoutReviewOrder'});
    this.showSpinner = false;
  }

  handleNotReadyCheckout(): void {
    if (this.paymentTypeIsCc()) {
      this.changeCCStatuses();
    }
    this.changeAttachmentsStatuses();
    this.showSpinner = false;
  }

  /**
   * Method updated b2b unit in case it contact, invoice recipient, ship to and bill to addresses are selected on data entry page
   */
  updateB2bUnit(): Observable<boolean> {
    return combineLatest([
      this.b2bUnitService.getCheckoutItContact(),
      this.b2bUnitService.getCheckoutInvoiceRecipient(),
      this.b2bUnitService.getCheckoutShipTo(),
      this.b2bUnitService.getCheckoutBillTo()
    ]).pipe(first(),
      switchMap(([itContactUid, invoiceRecipientUid, shipToAddressIdentity, billToAddressIdentity]) =>
        this.updateItContact(itContactUid).pipe(switchMap(() =>
          this.updateInvoiceRecipient(invoiceRecipientUid).pipe(switchMap(() =>
            this.updateShipToAddress(this.currentUserUid, shipToAddressIdentity).pipe(switchMap(() =>
              this.updateBillToAddress(this.currentUserUid, billToAddressIdentity).pipe(switchMap((cart) => {
                this.refreshCartService.refreshCart();
                if (cart) {
                  return this.activeCartService.getActiveCart().pipe(skip(1), take(1), map(() => true));
                } else {
                  return of(false);
                }
              }))
            ))
          ))
        ))),
      catchError((error) => {
        this.b2bUnitUpdateCallFailed.emit(true);
        return throwError(error);
      })
    );
  }

  private checkCouponSectionIsVisible(): void {
    this.featureToggleService.isEnabled(UNLEASH_TOGGLES.couponComponentVisible)
      .pipe(untilDestroyed(this))
      .subscribe((isToggleEnabled) =>
        this.isCouponSectionVisible = isToggleEnabled && (this.selectedView === this.view.Cart || this.activeCart.appliedVouchers?.length > 0));
  }

  private subscribeToCurrentPaymentProvider(): void {
    this.dcpCcPspService.getCurrentCartPaymentProviderId().pipe(
      first(),
      untilDestroyed(this)
    ).subscribe(paymentProvider => this.currentPaymentProvider = paymentProvider);
  }

  private updateItContact(itContactUid: string): Observable<Cart> {
    if (itContactUid) {
      return this.activeCartService.updateContact(AddressUsage.IT_CONTACT, this.currentUserUid, this.activeCart.code, itContactUid)
        .pipe(first());
    } else {
      return of(undefined);
    }
  };

  private updateInvoiceRecipient(invoiceRecipientUid: string): Observable<Cart> {
    if (invoiceRecipientUid) {
      return this.activeCartService.updateContact(AddressUsage.INVOICE_RECIPIENT, this.currentUserUid, this.activeCart.code, invoiceRecipientUid)
        .pipe(first());
    } else {
      return of(undefined);
    }
  }

  private updateShipToAddress(ownerUid: string, shipToAddressIdentity: AddressIdentity): Observable<Cart> {
    if (ownerUid && shipToAddressIdentity) {
      return this.activeCartService.updateAddress(AddressUsage.SHIP_TO, this.currentUserUid, this.activeCart.code, ownerUid, shipToAddressIdentity)
        .pipe(first());
    } else {
      return of(undefined);
    }
  }

  private updateBillToAddress(ownerUid: string, billToAddressIdentity: AddressIdentity): Observable<Cart> {
    if (ownerUid && billToAddressIdentity) {
      return this.activeCartService.updateAddress(AddressUsage.BILL_TO, this.currentUserUid, this.activeCart.code, ownerUid, billToAddressIdentity)
        .pipe(first());
    } else {
      return of(undefined);
    }
  }

  private getCurrentUser(): Observable<User> {
    return this.dcpApiService.callOcc(
      'currentUser',
      {},
      {},
      'GET'
    );
  }

  ngAfterViewInit(): void {
    this.scrollService.scrollTop();
  }

  private resetInvalidCouponError() {
    this.cartDecoratorErrorHandlingService.promotionRemovedErrorSubject$.next({showError:false, validation:{}});
    this.changeDetectorRef.detectChanges();
  }

  onPromoErrorNoCoupon() {
    this.isNoCouponPromoError = true;
    this.changeDetectorRef.detectChanges();
  }
}
